import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Cookies } from 'react-cookie';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';

import axiosInterceptor from 'api/interceptor/axiosClient';

interface DeleteDataModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteDataModal: React.FC<DeleteDataModalProps> = ({ isOpen, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [holdProgress, setHoldProgress] = useState(100);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const holdTimeout = useRef<NodeJS.Timeout | null>(null);
  const holdInterval = useRef<NodeJS.Timeout | null>(null);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleClose = useCallback(() => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setIsSuccess(false);
    setHoldProgress(100);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isSuccess) {
      closeTimeout.current = setTimeout(() => {
        handleClose();
      }, 2000);
    }

    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
    };
  }, [isSuccess, handleClose]);

  const handleDeleteHoldStart = () => {
    let progress = 100;
    setHoldProgress(100);
    setIsDeleting(true);
    setIsSuccess(false);

    holdInterval.current = setInterval(() => {
      progress -= 100 / 20;
      setHoldProgress(progress);
    }, 100);

    holdTimeout.current = setTimeout(async () => {
      try {
        const cookies = new Cookies();
        const token = cookies.get('local_access_token');
        await axiosInterceptor.post(
          '/user/soft-delete/',
          {},
          {
            headers: {
              Authorization: token,
            },
          },
        );

        setHoldProgress(0);
        setIsSuccess(true);
        setIsDeleting(false);
        enqueueSnackbar('Your data has been successfully deleted', {
          variant: 'success',
        });

        setTimeout(() => {
          cookies.remove('local_access_token');

          queryClient.clear();

          sessionStorage.clear();

          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', '/logout');
          }

          window.location.replace('/logout');
        }, 2000);
      } catch (error) {
        enqueueSnackbar('Failed to delete your data. Please try again.', {
          variant: 'error',
        });
        handleDeleteHoldEnd();
      }
    }, 2000);
  };

  const handleDeleteHoldEnd = () => {
    if (holdTimeout.current) clearTimeout(holdTimeout.current);
    if (holdInterval.current) clearInterval(holdInterval.current);
    setHoldProgress(100);
    setIsDeleting(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          width: '340px',
          position: 'relative',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="d-flex flex-column align-items-center justify-content-center gap-3">
          <div className="mb-1 text-center text-danger">
            Аre you sure you want to permanently delete your account and all associated data?
          </div>
          <p className="mb-0 text-center">
            This action will permanently delete your account, including all personal information, user profiles created
            by you, pest monitoring data, and any other records stored on our servers. This action is irreversible. Once
            deleted, your data cannot be recovered. If you're sure, please confirm below.
          </p>
          {!isSuccess ? (
            <button
              className="btn btn-danger w-100"
              onMouseDown={handleDeleteHoldStart}
              onMouseUp={handleDeleteHoldEnd}
              onMouseLeave={handleDeleteHoldEnd}
              onTouchStart={handleDeleteHoldStart}
              onTouchEnd={handleDeleteHoldEnd}
            >
              {isDeleting ? `Hold to confirm... ${Math.round((holdProgress / 100) * 2)}s` : 'Delete'}
            </button>
          ) : (
            <div className="text-success">Data deleted successfully!</div>
          )}
          {!isSuccess && (
            <button className="btn btn-secondary w-100" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteDataModal;
